import type { EndpointResponse } from '@rudi/api/schema';
import {
  CALENDAR_TASK_PAGE_SIZE,
  calendarFirstScreenPaths,
  calendarFirstScreenRequests,
  calendarMountWindow,
} from './calendarFirstScreen';

type Agenda = EndpointResponse<'/home/agenda', 'get'>;

export interface CalendarOpeningPart {
  url: string;
  field: Exclude<keyof Agenda, 'connections'>;
  wrapper?: 'events' | 'configs' | 'calendars';
}

export function calendarOpeningUrl(now: Date): string {
  const window = calendarMountWindow(now);
  return `/home/agenda?${new URLSearchParams({
    time_min: window.min.toISOString(),
    time_max: window.max.toISOString(),
  })}`;
}

export function calendarOpeningParts(now: Date): CalendarOpeningPart[] {
  const paths = calendarFirstScreenPaths(now);
  return [
    { url: paths.events, field: 'events', wrapper: 'events' },
    { url: paths.configs, field: 'configs', wrapper: 'configs' },
    { url: paths.recordsByStart, field: 'records_by_start' },
    { url: paths.recordsByDue, field: 'records_by_due' },
    { url: '/calendar-resources', field: 'calendars', wrapper: 'calendars' },
  ];
}

/** Keep each canonical body complete; an unavailable part falls back to its GET. */
export function projectCalendarOpening(body: unknown, part: CalendarOpeningPart): unknown {
  if (typeof body !== 'object' || body === null) throw new Error('Calendar opening unavailable');
  const value = (body as Partial<Agenda>)[part.field];
  if (part.wrapper) {
    if (!Array.isArray(value)) throw new Error('Calendar opening part unavailable');
    return { [part.wrapper]: value };
  }
  const page = value as Partial<Agenda['records_by_start']> | undefined;
  if (
    !page ||
    !Array.isArray(page.items) ||
    page.page !== 1 ||
    page.pageSize !== CALENDAR_TASK_PAGE_SIZE ||
    typeof page.total !== 'number' ||
    typeof page.totalPages !== 'number'
  )
    throw new Error('Calendar opening page unavailable');
  return page;
}

/** Fetch without the React client so the entry script can start the same read. */
export function startCalendarBootReads(
  now: Date,
  get: (url: string) => Promise<Response>
): Array<{ request: { url: string }; response: Promise<Response> }> {
  const agenda = get(calendarOpeningUrl(now)).then(async response => {
    if (!response.ok) throw new Error(`Calendar opening answered ${response.status}`);
    return {
      body: (await response.json()) as unknown,
      timing: response.headers.get('server-timing'),
    };
  });
  const responses = new Map(
    calendarOpeningParts(now).map(part => [
      part.url,
      agenda.then(({ body, timing }) => {
        const headers = new Headers({ 'Content-Type': 'application/json' });
        if (timing) headers.set('Server-Timing', timing);
        return new Response(JSON.stringify(projectCalendarOpening(body, part)), { headers });
      }),
    ])
  );
  // The aggregate omits link subscriptions, so Connections keeps its full route.
  responses.set('/calendar-shares', get('/calendar-shares'));
  responses.set('/calendar-connections', get('/calendar-connections'));
  return [...calendarFirstScreenRequests(now), '/calendar-resources', '/calendar-connections'].map(
    url => {
      const response = responses.get(url)!;
      response.catch(() => undefined);
      return { request: { url }, response };
    }
  );
}
